import { Box, Tab, Tabs } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDefinition } from '../../apps/definition';
import botsImage from '../../assets/images/bots.svg';
import { BotsModal } from '../../components/botsModal/BotsModal';
import { AuthorDetails } from '../../components/controls/AuthorDetails/AuthorDetails';
import { Loading } from '../../components/controls/Loading/Loading';
import { UIButton } from '../../components/controls/ui/UIButton/UIButton';
import { UIIcon } from '../../components/controls/ui/UIIcon/UIIcon';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/shadcn/lib/components/table';
import { Bot } from '../../models/Bots';
import { App } from '../../scripts/app';
import { useServiceAccount, useUserSafe } from '../../scripts/hooks';
import { useBots } from '../../scripts/hooks/bots';

export const BotsPage: React.FC = () => {
  const userApps = useUserSafe((user) => user.usersApps.nodes);
  const { bots, orgWideEnabledApps, loading, updateBot } = useBots();
  const slackDef = useAppDefinition('slack');
  const [currentBot, setCurrentBot] = useState<Bot | undefined>(undefined);

  const { serviceAccountEmail } = useServiceAccount();

  const closeModal = useCallback(() => {
    setCurrentBot(undefined);
  }, []);

  const isSlackInstalled = useMemo(
    () =>
      userApps.some((app) => app.appName === 'slack' && app.statusCode === 1),
    [userApps]
  );

  const slackApp = new App(slackDef!);

  return (
    <div className="mx-2 lg:mx-auto border-transparent my-24 max-w-5xl">
      <div className="flex gap-2 items-center">
        <UIIcon name="bot" size={32} />
        <h2 className="text-2xl m-0 font-bold">Bots</h2>
      </div>

      {!loading && bots.length === 0 && (
        <div className="mt-9 max-w-3xl">
          <div className="flex p-6 gap-4 rounded-lg bg-cloud-15 justify-between items-center mt-9 max-sm:p-4">
            <div className="p-2 bg-white rounded-full">
              <UIIcon name="info" size={24} />
            </div>
            <div>
              Bots you add in channels can automatically detect and answer
              questions using Dash AI in Slack ✨
            </div>
          </div>
          <img className="mt-10 w-full" src={botsImage} />
          <div className="flex flex-col gap-8 mt-6">
            <div className="flex">
              <InstructionItem completed={!!serviceAccountEmail} number={1} />
              <div>
                <div className="font-semibold text-black">
                  Setup a bot account{' '}
                  {serviceAccountEmail && `(${serviceAccountEmail})`}
                </div>
                <div className="text-cloud-40">
                  Bots use the account permissions of a bot account to find
                  answers to questions asked in Slack channels. An admin is
                  required to complete this step. Click{' '}
                  <a
                    className="inline-block"
                    href="https://dashworks.gitbook.io/dashworks-help-center/slackbot/slackbot-in-channels/setup-slackbot"
                    rel="noreferrer"
                    target="_blank"
                  >
                    here
                  </a>{' '}
                  to learn more.
                </div>
              </div>
            </div>
            <div className="flex">
              <InstructionItem completed={isSlackInstalled} number={2} />
              <div className="flex gap-4 items-center">
                <div className="font-semibold text-black">
                  Connect Slack from the App Store
                </div>
                {!isSlackInstalled && (
                  <UIButton
                    onClick={async () => slackApp.install(false)}
                    type="secondary"
                  >
                    Connect
                  </UIButton>
                )}
              </div>
            </div>
            <div className="flex">
              <InstructionItem number={3} />
              <div>
                <div className="font-semibold text-black">
                  Invite the Dashworks Slackbot by typing "/invite @Dashworks"
                  in a Slack channel
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {!loading && bots.length !== 0 && (
        <div>
          <div className="flex p-6 gap-4 rounded-lg bg-cloud-15 justify-between items-center mt-9 max-sm:p-4">
            <div className="p-2 bg-white rounded-full">
              <UIIcon name="info" size={24} />
            </div>
            <div>
              Bots can automatically answer questions in Slack channels using
              Dash AI. You can view, manage and invite bots from this page. Type
              "/invite @Dashworks" to invite the Slackbot in a channel.{' '}
              <a
                href="https://dashworks.gitbook.io/dashworks-help-center/slackbot/slackbot-in-channels"
                rel="noreferrer"
                target="_blank"
              >
                Learn more
              </a>
            </div>
          </div>
          <BotsTable bots={bots} setCurrentBot={setCurrentBot} />
        </div>
      )}
      {loading && <Loading />}

      {currentBot !== undefined && (
        <BotsModal
          bot={currentBot}
          closeModal={closeModal}
          orgWideEnabledApps={orgWideEnabledApps}
          submit={updateBot}
        />
      )}
    </div>
  );
};

interface BotsTableProps {
  bots: Bot[];
  setCurrentBot: (bot: Bot) => void;
}

const BotsTable = ({ bots, setCurrentBot }: BotsTableProps) => {
  const user = useUserSafe();
  const [activeTab, setActiveTab] = useState(0);
  const [filteredBots, setFilteredBots] = useState<Bot[]>(bots);

  const handleTabChange = useCallback(
    (_, newValue: number) => {
      setActiveTab(newValue);
      switch (newValue) {
        case 0: {
          setFilteredBots(bots);

          break;
        }

        case 1: {
          setFilteredBots(
            bots
              .filter((bot) => bot.user_id === user.userId)
              .sort((a, b) => b.created_at - a.created_at)
          );

          break;
        }
      }
    },
    [bots, user]
  );

  useEffect(() => {
    handleTabChange(null, activeTab);
  }, [activeTab, handleTabChange, bots]);

  return (
    <div>
      <Box
        className="mt-10 uiTabs MuiTabs-root Mui-selected"
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tabs onChange={handleTabChange} value={activeTab} variant="scrollable">
          <Tab
            label="All Bots"
            sx={{
              textTransform: 'none',
            }}
          />
          <Tab
            label="My Bots"
            sx={{
              textTransform: 'none',
            }}
          />
        </Tabs>
      </Box>
      {filteredBots.length === 0 && activeTab === 1 ? (
        <BlankStatePersonalTab />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-1/3">Channels</TableHead>
              <TableHead className="w-1/3">Invited by</TableHead>
              <TableHead className="w-1/3" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredBots.map((bot) => (
              <TableRow
                className="cursor-pointer"
                key={bot.id}
                onClick={() => {
                  setCurrentBot(bot);
                }}
              >
                <TableCell>
                  <div className="flex items-center gap-2">
                    <UIIcon name="slack" size={24} type="apps" />
                    {bot.bot_name}
                  </div>
                </TableCell>
                <TableCell>
                  <AuthorDetails
                    displayName={bot.author.display_name ?? bot.author.email}
                    icon={bot.author.icon}
                  />
                </TableCell>
                <TableCell />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

const BlankStatePersonalTab: React.FC = () => (
  <div className="flex flex-col justify-center items-center mt-16">
    <div className="text-2xl font-bold">No bots added yet</div>
    <div>Bots you add to channels will show up here on this tab</div>
  </div>
);

interface InstructionItemProps {
  number: number;
  completed?: boolean;
}

const InstructionItem: React.FC<InstructionItemProps> = ({
  number,
  completed,
}) => {
  if (completed)
    return (
      <div className="flex-shrink-0 bg-moss-30 text-white w-6 h-6 flex items-center justify-center rounded-full border-2 border-gray-300 text-sm font-semibold mr-3">
        <UIIcon name="check" size={16} />
      </div>
    );

  return (
    <div className="flex-shrink-0 bg-black text-black w-6 h-6 flex items-center justify-center rounded-full border-2 border-gray-300 text-sm font-semibold mr-3">
      <div className="text-white"> {number}</div>
    </div>
  );
};
